import { createDefu } from 'defu'
import { twMerge } from 'tailwind-merge'

export const defuStrConcat = createDefu((obj, key, value) => {
    type T = typeof obj // let defu handle the main generic
    if (typeof obj[key] === 'string' && typeof value === 'string') {
        obj[key] = (obj[key] + ' ' + value) as T[keyof T]
        return true
    }
    return false
})

/**
 * Tailwind-style "key" for a class token: strip leading `-` then take the first
 * segment (e.g. "left-0" -> "left", "-left-6" -> "left") so overrides match.
 */
function classTokenKey(token: string): string {
    const normalized = token.replace(/^-/, '')
    const segment = normalized.split('-')[0]
    return segment ?? normalized
}

/**
 * Merge two class strings so override classes win when they share the same key.
 * Keys are matched by prefix: "left" and "-left" are the same key so "-left-6"
 * overrides "left-0".
 */
export function mergeClassStrings(base: string, override: string): string {
    const baseTokens = base.trim().split(/\s+/).filter(Boolean)
    const overrideTokens = override.trim().split(/\s+/).filter(Boolean)
    const overrideKeys = new Set(overrideTokens.map(classTokenKey))
    const keptBase = baseTokens.filter((t) => !overrideKeys.has(classTokenKey(t)))
    return [...keptBase, ...overrideTokens].join(' ')
}

/**
 * Merges two `ui` slot maps (e.g. `UModal` / `UDrawer` `:ui` props) per key.
 * When both sides are class strings, uses `twMerge` so Tailwind utilities
 * resolve conflicts (e.g. `p-4` vs `px-0`); override values win over base.
 * Non-string values pass through like a shallow override (caller wins when set).
 */
export function mergeUiClassMap(
    base: Record<string, unknown>,
    overrides: Record<string, unknown>
): Record<string, unknown> {
    const keys = new Set([...Object.keys(base), ...Object.keys(overrides)])
    const out: Record<string, unknown> = {}

    for (const k of keys) {
        const b = base[k]
        const o = overrides[k]
        const bStr = typeof b === 'string'
        const oStr = typeof o === 'string'

        if (bStr && oStr) {
            out[k] = twMerge(b.trim(), o.trim())
            continue
        }
        if (oStr) {
            out[k] = o.trim()
            continue
        }
        if (bStr) {
            out[k] = b.trim()
            continue
        }
        if (o !== undefined) {
            out[k] = o
        } else if (b !== undefined) {
            out[k] = b
        }
    }

    return out
}
